import { useQuery } from '@tanstack/react-query'
import type {
  IInstrumentTemplateDetail,
  IInstrumentTemplateListResponse,
} from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'

export const instrumentTemplateKeys = {
  all: ['instrument-templates'] as const,
  list: (search: string) => [...instrumentTemplateKeys.all, 'list', search] as const,
  detail: (templateId: string) =>
    [...instrumentTemplateKeys.all, 'detail', templateId] as const,
}

export async function fetchInstrumentTemplates(
  search: string,
  signal?: AbortSignal,
): Promise<IInstrumentTemplateListResponse> {
  const q = search.trim()
  const { data: envelope } = await apiClient.get<
    ApiResponse<IInstrumentTemplateListResponse>
  >('/api/v1/instrument-templates', {
    signal,
    params: q ? { search: q } : undefined,
  })
  if (!envelope?.data) {
    throw new Error('Instrument templates response missing data')
  }
  return envelope.data
}

export async function fetchInstrumentTemplateById(
  templateId: string,
  signal?: AbortSignal,
): Promise<IInstrumentTemplateDetail> {
  const { data: envelope } = await apiClient.get<ApiResponse<IInstrumentTemplateDetail>>(
    `/api/v1/instrument-templates/${encodeURIComponent(templateId)}`,
    { signal },
  )
  if (!envelope?.data?.id) {
    throw new Error('Instrument template response missing data')
  }
  return envelope.data
}

export function useInstrumentTemplates(search: string) {
  return useQuery({
    queryKey: instrumentTemplateKeys.list(search),
    queryFn: ({ signal }) => fetchInstrumentTemplates(search, signal),
  })
}

export function useInstrumentTemplateDetail(
  templateId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: instrumentTemplateKeys.detail(templateId ?? ''),
    queryFn: ({ signal }) => fetchInstrumentTemplateById(templateId!, signal),
    enabled: enabled && Boolean(templateId),
  })
}
